import React, { useEffect, useId, useRef } from "react";
import ReactDOM from "react-dom";

// The one dialog shell for the site: overlay, header (title, optional subtitle, close button),
// scrolling body and an optional footer for actions. Closes on Escape and on a backdrop click
// unless `dismissible` is false (e.g. while a request is in flight).
// `variant="media"` drops the header and padding for image popups.
const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  footer,
  size = "md",
  variant,
  dismissible = true,
  children,
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape" && dismissible) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.classList.add("ui-modal-open");
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.classList.remove("ui-modal-open");
    };
  }, [isOpen, dismissible, onClose]);

  if (!isOpen) return null;

  const closeButton = (
    <button
      type="button"
      className="ui-modal__close"
      onClick={onClose}
      disabled={!dismissible}
      aria-label="Close"
    >
      <i className="ph ph-x" />
    </button>
  );

  return ReactDOM.createPortal(
    <div
      className="ui-modal"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && dismissible) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`ui-modal__dialog ui-modal__dialog--${size}${variant ? ` ui-modal__dialog--${variant}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {variant === "media" ? (
          <>
            {closeButton}
            {children}
          </>
        ) : (
          <>
            <header className="ui-modal__head">
              <div className="ui-modal__titles">
                {title && <h2 id={titleId} className="ui-modal__title">{title}</h2>}
                {subtitle && <p className="ui-modal__subtitle">{subtitle}</p>}
              </div>
              {closeButton}
            </header>
            {children && <div className="ui-modal__body">{children}</div>}
            {footer && <footer className="ui-modal__foot">{footer}</footer>}
          </>
        )}
      </div>
    </div>,
    document.getElementById("modal-root") || document.body
  );
};

export default Modal;
